import React from 'react';
import { BRAND } from '../config/brand.js';
import { useBrand } from '../context/TenantContext.jsx';

const SIZES = {
  sm: { badge: 26, fs: 10, wordSize: 12, subSize: 10 },
  md: { badge: 34, fs: 13, wordSize: 15, subSize: 11.5 },
  lg: { badge: 52, fs: 20, wordSize: 22, subSize: 16 },
  xl: { badge: 72, fs: 28, wordSize: 30, subSize: 21 },
};

const HEIGHTS = { sm: 26, md: 34, lg: 52, xl: 72 };

// First 1-3 uppercase initials of a name, for the badge glyph when a tenant has
// no uploaded logo (e.g. "Company A Connect" -> "CA").
function initials(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  const letters = words.map((w) => w[0]).join('').toUpperCase();
  return letters.slice(0, 3) || 'AFC';
}

// Colors come from the theme tokens (var(--brand-*)), which TenantProvider
// overrides per company — so the mark repaints with the tenant palette and, when
// the tenant sets a logo image or name, shows that instead of the defaults.
export default function BrandLogo({ size = 'md', variant = 'dark', className = '' }) {
  const s = SIZES[size] || SIZES.md;
  const brand = useBrand();
  const isLight = variant === 'light';
  const primaryColor = isLight ? '#ffffff' : 'var(--brand-primary)';
  const secondaryColor = isLight ? 'rgba(255,255,255,0.65)' : 'var(--brand-info)';
  const badgeBg = isLight ? 'rgba(255,255,255,0.18)' : 'var(--brand-primary)';
  const badgeText = '#ffffff';
  const badgeBorder = isLight ? 'rgba(255,255,255,0.35)' : 'transparent';

  // Uploaded tenant logo: show the image alongside the company name, so the
  // brand still reads as the company (the logo alone often isn't self-labeling).
  if (brand.logoUrl) {
    const logoName = brand.shortName || brand.name;
    return (
      <span
        className={`brand-logo ${className}`}
        style={{ display: 'inline-flex', alignItems: 'center', gap: s.badge * 0.28 }}
        aria-label={brand.name}
      >
        <img src={brand.logoUrl} alt={brand.name} style={{ height: HEIGHTS[size] || HEIGHTS.md, width: 'auto', objectFit: 'contain', flexShrink: 0 }} />
        {logoName && (
          <span style={{ fontWeight: 800, fontSize: s.wordSize, color: primaryColor, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
            {logoName}
          </span>
        )}
      </span>
    );
  }

  // Otherwise a wordmark. Default brand keeps its AFC / Group / Connect mark; a
  // tenant without a logo gets an initials badge + its display name.
  const { badge, word1, word2 } = brand.isTenant
    ? { badge: initials(brand.shortName || brand.name), word1: brand.shortName || brand.name, word2: '' }
    : BRAND.logo;

  return (
    <span
      className={`brand-logo ${className}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: s.badge * 0.28, textDecoration: 'none' }}
      aria-label={brand.name}
    >
      <svg
        width={s.badge}
        height={s.badge}
        viewBox="0 0 44 44"
        fill="none"
        aria-hidden="true"
        style={{ flexShrink: 0 }}
      >
        <rect width="44" height="44" rx="11" fill={badgeBg} />
        <rect x="0.5" y="0.5" width="43" height="43" rx="10.5" stroke={badgeBorder} />
        <text
          x="22"
          y="30"
          fill={badgeText}
          fontFamily="Inter, system-ui, sans-serif"
          fontSize={s.fs}
          fontWeight="900"
          textAnchor="middle"
          letterSpacing="-0.5"
        >
          {badge}
        </text>
      </svg>
      {(word1 || word2) && (
        <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
          {word1 && (
            <span style={{
              fontWeight: 800,
              fontSize: s.wordSize,
              color: primaryColor,
              letterSpacing: '-0.02em',
              lineHeight: 1,
            }}>
              {word1}
            </span>
          )}
          {word2 && (
            <span style={{
              fontWeight: 700,
              fontSize: s.subSize,
              color: secondaryColor,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              lineHeight: 1,
              marginTop: 2,
            }}>
              {word2}
            </span>
          )}
        </span>
      )}
    </span>
  );
}
